import { useNavigate } from 'react-router-dom'

import { Box, Button, Card, Divider } from '@factorialco/deprecated-design-system'

import DayPicker from 'modules/core/components/DayPicker'
import CheckBox from 'modules/core/components/Inputs/CheckBox'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useFormContext } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import Loading from 'modules/layout/components/Page/Loading'
import { DuplicateClassForm } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/DuplicateClass/types'

export const DuplicateClassModal = ({
  onCloseRoute,
  loading,
}: {
  onCloseRoute: string
  loading: boolean
}) => {
  const navigate = useNavigate()
  const { handleSubmit } = useFormContext<DuplicateClassForm>()
  const { isValid, values, setValues, errors } = useFormContext<DuplicateClassForm>()

  return (
    <Modal
      title={i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.title')}
      illustration="document-edit"
      description={i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.description')}
      onClose={() => navigate(onCloseRoute)}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button.Router to={onCloseRoute} type="secondary">
            {i18n.t('trainings.export_trainings.cancel')}
          </Button.Router>
          <Button
            submit
            disabled={!isValid}
            onClick={() => {
              track(trackingEvents.training.duplicate.saveDuplicateGroup, {
                group_costs: values.costs,
                group_participants: values.assignedParticipants,
                group_materials: values.materials,
              })
              handleSubmit()
            }}
            loading={loading}
          >
            {i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.button')}
          </Button>
        </Box>
      }
    >
      {loading ? (
        <Box height="full">
          <Loading />
        </Box>
      ) : (
        <Box gap="s24">
          <TextField
            value={values.name}
            label={i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.params.name')}
            mandatory
            onChange={(value) => {
              setValues({ ...values, name: value })
            }}
          />
          <Box flexDirection="row" gap="s16">
            <DayPicker
              label={i18n.t(
                'trainings.trainings_revamp.classes.duplicate_class_modal.params.start'
              )}
              value={values.startDate}
              onChange={(value) => setValues({ ...values, startDate: value })}
              mandatory
            />
            <DayPicker
              label={i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.params.end')}
              value={values.endDate}
              onChange={(value) => setValues({ ...values, endDate: value })}
              error={
                errors.endDate !==
                i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.params.mandatory')
                  ? errors.endDate
                  : undefined
              }
              mandatory
            />
          </Box>
          <Divider />
          <Card>
            <Card.Title
              size="small"
              action={
                <CheckBox
                  checked={values.sessions}
                  onClick={() => {
                    setValues({ ...values, sessions: !values.sessions })
                  }}
                  disabled
                />
              }
            >
              {i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.params.sessions')}
            </Card.Title>
          </Card>
          <Card>
            <Card.Title
              size="small"
              action={
                <CheckBox
                  checked={values.costs}
                  onClick={() => {
                    setValues({ ...values, costs: !values.costs })
                  }}
                />
              }
            >
              {i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.params.costs')}
            </Card.Title>
          </Card>
          <Card>
            <Card.Title
              size="small"
              action={
                <CheckBox
                  checked={values.assignedParticipants}
                  onClick={() => {
                    setValues({ ...values, assignedParticipants: !values.assignedParticipants })
                  }}
                />
              }
            >
              {i18n.t(
                'trainings.trainings_revamp.classes.duplicate_class_modal.params.participants'
              )}
            </Card.Title>
          </Card>
          <Card>
            <Card.Title
              size="small"
              action={
                <CheckBox
                  checked={values.materials}
                  onClick={() => {
                    setValues({ ...values, materials: !values.materials })
                  }}
                />
              }
            >
              {i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.params.materials')}
            </Card.Title>
          </Card>
        </Box>
      )}
    </Modal>
  )
}
